import {
  View,
  Text,
  StyleSheet,
  Keyboard,
  TouchableOpacity,
  Image,
  Platform,
} from "react-native";
import React, { useState, useEffect, useRef } from "react";
import { useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../utility/NavigationHelper";
import OTPTextView from "react-native-otp-textinput";
import * as Clipboard from "expo-clipboard";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { toast } from "react-toastify";
import { ShowLoader } from "../../../common/ShowLoader";
import { resendVerificationCode, verifyUser, login } from "../../../services/authAPI";
import { AppFontStyle } from "../../../styles/AppFontStyle";
import { AppColors } from "../../../utility/AppColors";
import { AppImages } from "../../../utility/AppImages";
import { AppScreens } from "../../Utility/AppScreens";
import { ScreenBGGradientView } from "../../../common/CommonView";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { storeLoginData } from "../../../utility/AppStoreData";
import { getUserLocationDetails } from "../../../common/getLocation";
import { sendGTMEvent } from "../../../utility/gtm";

const EmailConfirmationWebScreen = ({ route }) => {
  const routeData = route.params;
  const navigation = useNavigation();
  const [otpInput, setOtpInput] = useState("");
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [showLoader, setShowLoader] = useState(false);
  const otpRef = useRef(null);

  // Dynamic title based on auth type (phone or email)
  const authType = routeData?.authType || 'email';
  const verificationTitle = authType === 'phone' ? 'WhatsApp Verification' : 'Email Confirmation';

  useEffect(() => {
    const handlePaste = async (event) => {
      try {
        let text;
        // Use navigator.clipboard for web, expo-clipboard for mobile
        if (typeof navigator !== "undefined" && navigator.clipboard) {
          text = await navigator.clipboard.readText();
        } else {
          text = await Clipboard.getStringAsync();
        }

        if (text && /^\d{6}$/.test(text)) {
          setOtpInput(text);
          if (otpRef.current) {
            otpRef.current.setValue(text);
          }
        }
      } catch (error) {
        console.log("Paste error:", error);
      }
    };

    // Add paste event listener for web
    if (typeof window !== "undefined") {
      window.addEventListener("paste", handlePaste);
      return () => {
        window.removeEventListener("paste", handlePaste);
      };
    }
  }, []);

  // Paste from clipboard function for mobile
  const pasteFromClipboard = async () => {
    try {
      const text = await Clipboard.getStringAsync();
      if (text) {
        // Extract only digits
        const digits = text.replace(/\D/g, '');
        if (digits.length >= 6) {
          const otp = digits.substring(0, 6);
          setOtpInput(otp);
          if (otpRef.current) {
            otpRef.current.setValue(otp);
          }
        } else if (digits.length > 0) {
          setOtpInput(digits);
          if (otpRef.current) {
            otpRef.current.setValue(digits);
          }
        }
      }
    } catch (error) {
      console.log('Paste error:', error);
    }
  };

  const handleOTPSubmit = () => {
    if (otpInput.length !== 6) {
      toast.error("Please enter a valid OTP");
    } else {
      const param = {
        username: routeData.username,
        confirmationCode: otpInput,
      };
      userVerifyAPI({
        bodyParams: param,
        success: (data) => {
          toast.success(data || "Email verification successful");

          // Check if this is from login flow
          const fromLogin = routeData?.fromLogin === true;

          if (fromLogin) {
            // Auto-login the user after verification when coming from login flow
            handleAutoLoginAfterVerification();
          } else {
            // Original signup flow - navigate to choose avatar screen
            // Fire GTM event for new signup
            sendGTMEvent('Platform-New-Signup');
            
            navigateToScreen(navigation, AppScreens.ChooseAvatarScreen, {
              username: routeData.username,
            });
          }
        },
      });
    }
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Enter" && otpInput.length === 6) {
        handleOTPSubmit();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyPress);
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }
  }, [otpInput]);

  const userVerifyAPI = async (props) => {
    const { bodyParams = {}, success } = props;

    try {
      setShowLoader(true);
      const data = await verifyUser(bodyParams);
      const responseBody = data.body;
      setShowLoader(false);

      if (data.statusCode === 200 || data.statusCode === 201) {
        success(responseBody);
      } else {
        toast.error(responseBody?.error || "Verification Failed");
      }
    } catch (error) {
      setShowLoader(false);
      toast.error(error.message);
    }
  };

  const resendCode = async (username) => {
    try {
      setShowLoader(true);
      const data = await resendVerificationCode(username);
      const responseBody = data.body;
      setShowLoader(false);

      if (data.statusCode === 200 || data.statusCode === 201) {
        toast.success(
          responseBody?.message || "Verification code sent successfully"
        );
      } else {
        toast.error(responseBody?.error || "Resend Failed");
      }
    } catch (error) {
      setShowLoader(false);
      toast.error(error.message);
    }
  };

  const handleAutoLoginAfterVerification = async () => {
    try {
      setShowLoader(true);

      // Get the password from cookie
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2)
          return decodeURIComponent(parts.pop().split(";").shift());
        return null;
      };

      const password = getCookie("password");

      if (!password) {
        toast.error("Session expired. Please login again.");
        setShowLoader(false);
        navigateToScreen(navigation, AppScreens.LoginWebScreen);
        return;
      }

      const params = {
        username: routeData.username,
        password: password,
      };

      const data = await login(params);

      if (data.status === 200) {
        toast.success("Login successful");
        storeLoginData(data.body);
        await AsyncStorage.setItem("username", routeData.username);
        await getUserLocationDetails();

        // Clear the password cookie
        document.cookie = "password=; path=/; max-age=0;";

        // Clear sessionStorage
        sessionStorage.removeItem("pendingLoginUsername");

        // Navigate to home
        navigateToScreen(navigation, AppScreens.DrawerRoute, {
          username: routeData.username,
        });
      } else {
        toast.error("Login failed. Please try again.");
        navigateToScreen(navigation, AppScreens.LoginWebScreen);
      }
    } catch (error) {
      console.log("Auto-login error:", error);
      toast.error(error.message || "Login failed. Please try again.");
      navigateToScreen(navigation, AppScreens.LoginWebScreen);
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <View style={styles.container}>
      <ScreenBGGradientView />
      <View
        style={{
          ...(deviceSize === DEVICE_SIZES.XS ||
            deviceSize === DEVICE_SIZES.SM ||
            deviceSize === DEVICE_SIZES.MD ||
            deviceSize === DEVICE_SIZES.LG
            ? { flex: 1, justifyContent: "flex-start" }
            : { flex: 0.4, justifyContent: "center" }),
        }}
      >
        {deviceSize === DEVICE_SIZES.XS ||
          deviceSize === DEVICE_SIZES.SM ||
          deviceSize === DEVICE_SIZES.MD ||
          deviceSize === DEVICE_SIZES.LG ? (
          <View style={{ alignItems: "center", marginTop: 10 }}>
            <View
              style={{
                borderRadius: 12,
                width: "90%",
                maxWidth: 380,
                height: 260,
                elevation: 20,
              }}
            >
              <Image
                source={AppImages.icMainLogo}
                style={{
                  resizeMode: "contain",
                  width: "100%",
                  height: "100%",
                }}
              />
            </View>
          </View>
        ) : null}

        <View
          style={{
            paddingLeft:
              deviceSize === DEVICE_SIZES.XS ||
                deviceSize === DEVICE_SIZES.SM ||
                deviceSize === DEVICE_SIZES.MD ||
                deviceSize === DEVICE_SIZES.LG
                ? "5%"
                : "15%",
            paddingRight:
              deviceSize === DEVICE_SIZES.XS ||
                deviceSize === DEVICE_SIZES.SM ||
                deviceSize === DEVICE_SIZES.MD ||
                deviceSize === DEVICE_SIZES.LG
                ? "5%"
                : 0,
          }}
        >
          <Text
            style={[
              deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                ? AppFontStyle.BOLD_ANYBODY_25
                : AppFontStyle.BOLD_ANYBODY_32,
              {
                color: AppColors.white,
                marginVertical: 15,
                textAlign:
                  deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM ||
                    deviceSize === DEVICE_SIZES.MD ||
                    deviceSize === DEVICE_SIZES.LG
                    ? "center"
                    : "left",
                ...(deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM ||
                  deviceSize === DEVICE_SIZES.MD ||
                  deviceSize === DEVICE_SIZES.LG
                  ? { marginTop: 30 }
                  : {}),
              },
            ]}
          >
            {verificationTitle}
          </Text>

          <View>
            <OTPTextView
              ref={otpRef}
              handleTextChange={setOtpInput}
              offTintColor={AppColors.appBlueColor_3A5175}
              tintColor={AppColors.themeColor_0A89AD}
              containerStyle={styles.textInputContainer}
              textInputStyle={styles.roundedTextInput}
              inputCount={6}
              inputCellLength={1}
              keyboardType="numeric"
            />
            {/* Paste from clipboard button - only show on mobile */}
            {Platform.OS !== 'web' && (
              <TouchableOpacity
                onPress={pasteFromClipboard}
                style={{
                  alignSelf: 'center',
                  marginTop: 10,
                  paddingVertical: 8,
                  paddingHorizontal: 16,
                  backgroundColor: AppColors.appBlueColor_3A5175,
                  borderRadius: 8,
                }}
              >
                <Text style={{ color: AppColors.white, fontSize: 14 }}>
                  Paste from clipboard
                </Text>
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.resetCodeText}>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_14,
                {
                  color: "white",
                },
              ]}
            >
              Didn't get the code?{" "}
            </Text>
            <TouchableOpacity
              onPress={() => {
                resendCode(routeData.username);
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_14,
                  {
                    color: "white",
                  },
                ]}
              >
                Resend code
              </Text>
            </TouchableOpacity>
          </View>

          <View
            style={{
              height: 40,
              alignItems: "center",
            }}
          >
            <TouchableOpacity
              style={{
                height: 40,
                width: 250,
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: AppColors.buttonRed_DC2626,
                borderRadius: 20,
              }}
              onPress={handleOTPSubmit}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_14,
                  { color: AppColors.white },
                ]}
              >
                Confirm
              </Text>
            </TouchableOpacity>
          </View>
          <View
            style={{
              height: 40,
              alignItems: "center",
            }}
          >
            <TouchableOpacity
              style={{
                height: 40,
                width: 250,
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 20,
              }}
              onPress={() => {
                // Clear the password cookie when user goes back
                document.cookie = "password=; path=/; max-age=0;";
                sessionStorage.removeItem("pendingLoginUsername");

                // Navigate based on where user came from
                if (routeData?.fromLogin) {
                  navigateToScreen(navigation, AppScreens.LoginWebScreen);
                } else {
                  navigateToScreen(navigation, AppScreens.SignupWebScreen);
                }
              }}
            >
              <Text
                style={[
                  AppFontStyle.MEDIUM_ANYBODY_12,
                  { color: AppColors.white },
                ]}
              >
                {routeData?.fromLogin ? "Go back to Login" : "Go back to Sign up"}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
      {deviceSize === DEVICE_SIZES.XS ||
        deviceSize === DEVICE_SIZES.SM ||
        deviceSize === DEVICE_SIZES.MD ||
        deviceSize === DEVICE_SIZES.LG ? null : (
        <View
          style={{
            position: "relative",
            justifyContent: "center",
            alignItems: "center",
            paddingRight: "5%",
            flex: 0.6,
          }}
        >
          <View
            style={{
              borderRadius: 12,
              elevation: 20,
            }}
          >
            <Image
              source={AppImages.icMainLogo}
              style={{
                width: 764,
                height: 468,
              }}
              resizeMode="contain"
            />
          </View>
          <View
            style={{
              flexDirection: "row",
              position: "absolute",
              bottom: 20,
              right: 30,
              alignItems: "center",
            }}
          >
            <Image
              source={AppImages.icYoutubeLogo}
              style={{
                width: 56,
                height: 56,
              }}
              resizeMode="contain"
            />
            <Image
              source={AppImages.icTwitchLogo}
              style={{
                width: 56,
                height: 56,
              }}
              resizeMode="contain"
            />
          </View>
        </View>
      )}
      <View
        style={{
          flexDirection: "row",
          position: "absolute",
          bottom: 20,
          right: 30,
          alignItems: "center",
        }}
      >
        <Image
          source={AppImages.icYoutubeLogo}
          style={{
            width: 56,
            height: 56,
          }}
          resizeMode="contain"
        />
        <Image
          source={AppImages.icTwitchLogo}
          style={{
            width: 56,
            height: 56,
          }}
          resizeMode="contain"
        />
      </View>

      <ShowLoader showIndicater={showLoader} isBackGround={true} />
    </View>
  );
};

export default EmailConfirmationWebScreen;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      flexDirection: "row",
      justifyContent: "space-between",
      gap: 30,
    },
    textInputContainer: {
      height: 60,
    },
    roundedTextInput: {
      borderRadius: 14,
      borderWidth: 1,
      borderBottomWidth: 1,
      alignContent: "center",
      backgroundColor: AppColors.appBlueColor_3A5175,
      fontSize: 16,
      color: AppColors.white,
      elevation: 2,
      width: 60,
      height: 60,
    },
    resetCodeText: {
      marginHorizontal: 10,
      flexDirection: "row",
      marginVertical: 25,
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      container: {
        flex: 1,
        flexDirection: "row",
      },
      textInputContainer: {
        height: 60,
        justifyContent: "center",
        alignContent: "space-between",
      },
      roundedTextInput: {
        borderRadius: 14,
        borderWidth: 1,
        borderBottomWidth: 1,
        alignContent: "space-between",
        backgroundColor: AppColors.appBlueColor_3A5175,
        fontSize: 16,
        color: AppColors.white,
        elevation: 2,
        width: 60,
        height: 60,
      },
      resetCodeText: {
        marginHorizontal: 10,
        flexDirection: "row",
        justifyContent: "center",
        marginVertical: 25,
      },
    },
    [DEVICE_SIZES.MD]: {
      container: {
        flex: 1,
        flexDirection: "row",
      },
      textInputContainer: {
        height: 60,
        justifyContent: "center",
        alignContent: "space-between",
      },
      roundedTextInput: {
        borderRadius: 14,
        borderWidth: 1,
        borderBottomWidth: 1,
        alignContent: "space-between",
        backgroundColor: AppColors.appBlueColor_3A5175,
        fontSize: 16,
        color: AppColors.white,
        elevation: 2,
        width: 60,
        height: 60,
      },
      resetCodeText: {
        flexDirection: "row",
        justifyContent: "center",
        marginVertical: 25,
      },
    },
    [DEVICE_SIZES.SM]: {
      container: {
        flex: 1,
        flexDirection: "row",
      },
      textInputContainer: {
        height: 45,
        justifyContent: "center",
        alignContent: "space-between",
      },
      roundedTextInput: {
        borderRadius: 14,
        borderWidth: 1,
        borderBottomWidth: 1,
        alignContent: "space-between",
        backgroundColor: AppColors.appBlueColor_3A5175,
        fontSize: 16,
        color: AppColors.white,
        elevation: 2,
        width: 45,
        height: 45,
      },
      resetCodeText: {
        flexDirection: "row",
        justifyContent: "center",
        marginVertical: 25,
      },
    },
    [DEVICE_SIZES.XS]: {
      container: {
        flex: 1,
        flexDirection: "row",
      },
      textInputContainer: {
        height: 55,
        justifyContent: "center",
        alignContent: "space-between",
      },
      roundedTextInput: {
        borderRadius: 14,
        borderWidth: 1,
        borderBottomWidth: 1,
        alignContent: "space-between",
        backgroundColor: AppColors.appBlueColor_3A5175,
        fontSize: 16,
        color: AppColors.white,
        elevation: 2,
        width: 50,
        height: 55,
      },
      resetCodeText: {
        marginHorizontal: 30,
        flexDirection: "row",
        marginVertical: 25,
        justifyContent: "center",
      },
    },
  }
);
